import * as React from "react";
import {
    BrowserRouter,
    Routes,
    Route,
    useNavigate,
    Navigate,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useCookies } from "react-cookie";

import useToken from "./hooks/useToken";

import Settings from "./pages/Settings/SettingsIndex";
import Login from "./pages/Login";
import Logout from "./pages/Logout";
import PlanVisualizer from "./pages/Automation/AutomationIndex";
import StatusViewer from "./pages/Monitoring/index";
import AlarmViewer from "./pages/Alerts/AlertsIndex";
import Trends from "./pages/Trends/TrendsIndex";
import Savings from "./pages/Savings";
import Metering from "./pages/Metering/MeteringIndex";
import NewMetering from "./pages/NewMetering/MeteringIndex";
import NewOccupancy from "./pages/NewOccupancy/OccupancyIndex";

import PageWithDrawerFacility from "./components/PageWithDrawerFacility";

import FacilityApi from "./api/FacilityApi";
import AuthApi from "./api/AuthApi";
import ClientApi from "./api/ClientApi";
import PlanApi from "./api/PlanApi";
import ReadingsApi from "./api/ReadingsApi";
import SolarApi from "./api/SolarApi";
import ControllerApi from "./api/ControllerApi";
import TariffApi from "./api/TariffApi";
import AlertsApi from "./api/AlertsApi";
import AlarmsApi from "./api/AlarmsApi";
import PointsApi from "./api/PointsApi";
import EventsApi from "./api/EventsApi";
import HvacApi from "./api/HvacApi";
import GeneratorApi from "./api/GeneratorApi";
import FloorPlansApi from "./api/FloorPlansApi";
import ConstraintsApi from "./api/ConstraintsApi";
import NewOccupancyApi from "./api/NewOccupancyApi";
import MeteringTariffApi from "./api/MeteringTariffApi";
import PowerSensorPointApi from "./api/PowerSensorPointApi";
import GroupApi from "./api/GroupApi";
import GraphqlApi from "./api/GraphqlApi";

import { ALLOWED_PATHS, APP_DEFAULT_HOME_PAGE_PATH } from "./util/pagePaths";

export const ApiContext = React.createContext({});

function App() {
    const client_id = window.location.host.split(".")[0];

    const hasNavigatedRef = React.useRef(false);
    const currentPathRef = React.useRef(window.location.pathname);

    const navigate = useNavigate();

    const [cookies, setCookie, removeCookie] = useCookies();

    let apiKey = undefined;
    if (cookies[`${client_id}_apikey`]) {
        apiKey = cookies[`${client_id}_apikey`];
    }

    const { token, setToken } = useToken(apiKey);

    const { t, i18n } = useTranslation();

    const [language, setLanguage] = React.useState(i18n.resolvedLanguage);

    const [clientInfo, setClientInfo] = React.useState(undefined);

    const [facilities, setFacilities] = React.useState(undefined);

    const [selectedFacility, setSelectedFacility] = React.useState(undefined);

    const [portfolioView, setPortfolioView] = React.useState(false);

    const [searchParams, setSearchParams] = useSearchParams();

    const handleUnauthorized = () => {
        setToken(undefined);
    };

    const facilityApi = new FacilityApi(token, client_id, handleUnauthorized);
    const authApi = new AuthApi(token, client_id, handleUnauthorized);
    const clientApi = new ClientApi(token, client_id, handleUnauthorized);
    const planApi = new PlanApi(token, client_id, handleUnauthorized);
    const readingsApi = new ReadingsApi(token, client_id, handleUnauthorized);
    const solarApi = new SolarApi(token, client_id, handleUnauthorized);
    const controllerApi = new ControllerApi(
        token,
        client_id,
        handleUnauthorized,
    );
    const tariffApi = new TariffApi(token, client_id, handleUnauthorized);
    const alertsApi = new AlertsApi(token, client_id, handleUnauthorized);
    const alarmsApi = new AlarmsApi(token, client_id, handleUnauthorized);
    const pointsApi = new PointsApi(token, client_id, handleUnauthorized);
    const eventsApi = new EventsApi(token, client_id, handleUnauthorized);
    const generatorApi = new GeneratorApi(token, client_id, handleUnauthorized);
    const floorPlanApi = new FloorPlansApi(
        token,
        client_id,
        handleUnauthorized,
    );
    const constraintsApi = new ConstraintsApi(
        token,
        client_id,
        handleUnauthorized,
    );
    const hvacApi = new HvacApi(token, client_id, handleUnauthorized);
    const newOccupancyApi = new NewOccupancyApi(
        token,
        client_id,
        handleUnauthorized,
    );
    const meteringTariffApi = new MeteringTariffApi(
        token,
        client_id,
        handleUnauthorized,
    );
    const powerSensorPointApi = new PowerSensorPointApi(
        token,
        client_id,
        handleUnauthorized,
    );
    const groupApi = new GroupApi(token, client_id, handleUnauthorized);
    const graphqlApi = new GraphqlApi(token, client_id, handleUnauthorized);

    React.useEffect(() => {
        clientApi.getClient(
            (data) => {
                setClientInfo(data);
            },
            () => {},
        );
        facilityApi.list(
            (data) => {
                setFacilities(data);

                if (data.length === 0) {
                    return;
                }

                if (data.length > 1) setPortfolioView(true);
                if (searchParams.get("facility")) {
                    if (
                        data.filter(
                            (d) =>
                                d.url.split("/").slice(-2)[0] ===
                                searchParams.get("facility").toString(),
                        )
                    ) {
                        setSelectedFacility(
                            data.find(
                                (d) =>
                                    d.url.split("/").slice(-2)[0] ===
                                    searchParams.get("facility").toString(),
                            ).url,
                        );
                    } else {
                        setSelectedFacility(data[0].url);
                    }
                } else {
                    const storedSelectedFacility =
                        localStorage.getItem("selectedFacility");
                    if (storedSelectedFacility) {
                        if (
                            data.filter((d) => d.url === storedSelectedFacility)
                        ) {
                            setSelectedFacility(storedSelectedFacility);
                        } else {
                            setSelectedFacility(data[0].url);
                            localStorage.setItem(
                                "selectedFacility",
                                data[0].url,
                            );
                        }
                    } else {
                        setSelectedFacility(data[0].url);
                        localStorage.setItem("selectedFacility", data[0].url);
                    }
                }
            },
            () => {
                alert(t("Failed to fetch facilities"));
            },
            () => {
                alert(
                    t("Your account isn't authorized to perform this action."),
                );
            },
        );
    }, [token]);

    React.useEffect(() => {
        if (
            facilities &&
            facilities.length > 0 &&
            searchParams.get("facility")
        ) {
            if (
                facilities.filter(
                    (d) =>
                        d.url.split("/").slice(-2)[0] ===
                        searchParams.get("facility").toString(),
                )
            ) {
                setSelectedFacility(
                    facilities.find(
                        (d) =>
                            d.url.split("/").slice(-2)[0] ===
                            searchParams.get("facility").toString(),
                    ).url,
                );
            } else {
                setSelectedFacility(facilities[0].url);
            }
        }
    }, [searchParams]);

    React.useEffect(() => {
        i18n.changeLanguage(language);
    }, [language]);

    const handleLogout = () => {
        removeCookie(`${client_id}_apikey`, {
            path: "/",
            domain:
                "." + window.location.hostname.split(".").slice(1).join("."),
        });

        authApi.logout(
            () => {
                setToken(undefined);
            },
            () => {
                setToken(undefined);
            },
        );

        const logOutURL = authApi.getLogOutURL();

        window.location.replace(logOutURL);
    };

    const handleLanguageChange = (newVal) => {
        setLanguage(newVal);
        localStorage.setItem("language", newVal);
    };

    const handleFacilityChange = (newVal) => {
        setSelectedFacility(newVal);
        localStorage.setItem("selectedFacility", newVal);

        if (searchParams.get("facility"))
            searchParams.set("facility", newVal.split("/").slice(-2)[0]);
        setSearchParams(searchParams);
        setPortfolioView(false);
    };

    return (
        <ApiContext.Provider
            value={{
                facilityApi,
                authApi,
                clientApi,
                planApi,
                readingsApi,
                solarApi,
                controllerApi,
                tariffApi,
                alertsApi,
                alarmsApi,
                pointsApi,
                eventsApi,
                generatorApi,
                floorPlanApi,
                constraintsApi,
                hvacApi,
                newOccupancyApi,
                meteringTariffApi,
                powerSensorPointApi,
                groupApi,
                graphqlApi,
            }}
        >
            {token ? (
                clientInfo && facilities ? (
                    facilities.length > 0 ? (
                        <Routes>
                            <Route
                                index
                                element={
                                    clientInfo ? (
                                        <>
                                            <Navigate
                                                to={
                                                    "/" +
                                                    (ALLOWED_PATHS.includes(
                                                        clientInfo.home_page_path,
                                                    )
                                                        ? clientInfo.home_page_path
                                                        : APP_DEFAULT_HOME_PAGE_PATH)
                                                }
                                                replace
                                            />
                                        </>
                                    ) : null
                                }
                            />
                            <Route
                                path="/status"
                                element={
                                    <PageWithDrawerFacility
                                        title={t("Status")}
                                        element={
                                            <StatusViewer
                                                clientInfo={clientInfo}
                                                facility={selectedFacility}
                                            />
                                        }
                                        onLogout={handleLogout}
                                        language={language}
                                        onLanguageChange={handleLanguageChange}
                                        facilities={facilities}
                                        selectedFacility={selectedFacility}
                                        onFacilityChange={handleFacilityChange}
                                        hiddenPages={
                                            clientInfo?.hidden_pages ?? []
                                        }
                                    />
                                }
                            />
                            <Route
                                path="/metering"
                                element={
                                    <PageWithDrawerFacility
                                        title={t("Metering")}
                                        element={
                                            <Metering
                                                clientInfo={clientInfo}
                                                facility={selectedFacility}
                                                portfolioView={portfolioView}
                                            />
                                        }
                                        onLogout={handleLogout}
                                        language={language}
                                        onLanguageChange={handleLanguageChange}
                                        facilities={facilities}
                                        selectedFacility={selectedFacility}
                                        onFacilityChange={handleFacilityChange}
                                        hiddenPages={
                                            clientInfo?.hidden_pages ?? []
                                        }
                                        portfolioView={portfolioView}
                                        onSelectPortfolioView={() =>
                                            setPortfolioView(true)
                                        }
                                    />
                                }
                            />
                            <Route
                                path="/newmetering"
                                element={
                                    searchParams.get("staff") === "true" ? (
                                        <PageWithDrawerFacility
                                            title={t("Metering")}
                                            element={
                                                <NewMetering
                                                    clientInfo={clientInfo}
                                                    facility={selectedFacility}
                                                    portfolioView={portfolioView}
                                                />
                                            }
                                            onLogout={handleLogout}
                                            language={language}
                                            onLanguageChange={handleLanguageChange}
                                            facilities={facilities}
                                            selectedFacility={selectedFacility}
                                            onFacilityChange={handleFacilityChange}
                                            hiddenPages={
                                                clientInfo?.hidden_pages ?? []
                                            }
                                            portfolioView={portfolioView}
                                            onSelectPortfolioView={() =>
                                                setPortfolioView(true)
                                            }
                                        />
                                    ) : (
                                        <Navigate to="/metering" replace />
                                    )
                                }
                            />
                            <Route
                                path="/settings"
                                element={
                                    <PageWithDrawerFacility
                                        title={t("Settings")}
                                        element={
                                            <Settings
                                                clientInfo={clientInfo}
                                                facility={selectedFacility}
                                            />
                                        }
                                        onLogout={handleLogout}
                                        language={language}
                                        onLanguageChange={handleLanguageChange}
                                        facilities={facilities}
                                        selectedFacility={selectedFacility}
                                        onFacilityChange={handleFacilityChange}
                                        hiddenPages={
                                            clientInfo?.hidden_pages ?? []
                                        }
                                    />
                                }
                            />
                            <Route
                                path="/automation"
                                element={
                                    <PageWithDrawerFacility
                                        title={t("Automation")}
                                        element={
                                            <PlanVisualizer
                                                clientInfo={clientInfo}
                                                facility={selectedFacility}
                                                facilityInfo={facilities.find(
                                                    (item) =>
                                                        item.url ===
                                                        selectedFacility,
                                                )}
                                            />
                                        }
                                        onLogout={handleLogout}
                                        language={language}
                                        onLanguageChange={handleLanguageChange}
                                        facilities={facilities}
                                        selectedFacility={selectedFacility}
                                        onFacilityChange={handleFacilityChange}
                                        hiddenPages={
                                            clientInfo?.hidden_pages ?? []
                                        }
                                    />
                                }
                            />
                            <Route
                                path="/monitoring"
                                element={
                                    <PageWithDrawerFacility
                                        title={t("Monitoring")}
                                        element={
                                            <StatusViewer
                                                clientInfo={clientInfo}
                                                facility={selectedFacility}
                                            />
                                        }
                                        onLogout={handleLogout}
                                        language={language}
                                        onLanguageChange={handleLanguageChange}
                                        facilities={facilities}
                                        selectedFacility={selectedFacility}
                                        onFacilityChange={handleFacilityChange}
                                        hiddenPages={
                                            clientInfo?.hidden_pages ?? []
                                        }
                                    />
                                }
                            />
                            <Route
                                path="/alerts"
                                element={
                                    <PageWithDrawerFacility
                                        title={t("Alerts")}
                                        element={
                                            <AlarmViewer
                                                clientInfo={clientInfo}
                                                facility={selectedFacility}
                                            />
                                        }
                                        onLogout={handleLogout}
                                        language={language}
                                        onLanguageChange={handleLanguageChange}
                                        facilities={facilities}
                                        selectedFacility={selectedFacility}
                                        onFacilityChange={handleFacilityChange}
                                        hiddenPages={
                                            clientInfo?.hidden_pages ?? []
                                        }
                                    />
                                }
                            />
                            <Route
                                path="/trends"
                                element={
                                    <PageWithDrawerFacility
                                        title={t("Trends")}
                                        element={
                                            <Trends
                                                clientInfo={clientInfo}
                                                facility={selectedFacility}
                                                facilities={facilities}
                                            />
                                        }
                                        onLogout={handleLogout}
                                        language={language}
                                        onLanguageChange={handleLanguageChange}
                                        facilities={facilities}
                                        selectedFacility={selectedFacility}
                                        onFacilityChange={handleFacilityChange}
                                        hiddenPages={
                                            clientInfo?.hidden_pages ?? []
                                        }
                                    />
                                }
                            />
                            <Route
                                path="/logout"
                                element={<Logout onLogout={handleLogout} />}
                            />
                            <Route
                                path="/savings"
                                element={
                                    <PageWithDrawerFacility
                                        title={t("Estimated savings")}
                                        element={
                                            <Savings
                                                clientInfo={clientInfo}
                                                facility={selectedFacility}
                                                facilities={facilities}
                                            />
                                        }
                                        onLogout={handleLogout}
                                        language={language}
                                        onLanguageChange={handleLanguageChange}
                                        facilities={facilities}
                                        selectedFacility={selectedFacility}
                                        onFacilityChange={handleFacilityChange}
                                        hiddenPages={
                                            clientInfo?.hidden_pages ?? []
                                        }
                                    />
                                }
                            />
                            <Route
                                path="/occupancy"
                                element={
                                    <PageWithDrawerFacility
                                        title={t("Occupancy")}
                                        element={
                                            <NewOccupancy
                                                clientInfo={clientInfo}
                                                facility={selectedFacility}
                                            />
                                        }
                                        onLogout={handleLogout}
                                        language={language}
                                        onLanguageChange={handleLanguageChange}
                                        facilities={facilities}
                                        selectedFacility={selectedFacility}
                                        onFacilityChange={handleFacilityChange}
                                        hiddenPages={
                                            clientInfo?.hidden_pages ?? []
                                        }
                                    />
                                }
                            />
                        </Routes>
                    ) : (
                        <h3>
                            Successful login. Contact admin to request
                            permissions.
                        </h3>
                    )
                ) : null
            ) : (
                <Login setToken={setToken} />
            )}
        </ApiContext.Provider>
    );
}

export default function RouterApp() {
    return (
        <BrowserRouter>
            <App />
        </BrowserRouter>
    );
}
